import React from 'react'
import {
    ChangeEvent,
    DragEvent,
    KeyboardEvent,
    ReactNode,
    RefObject,
} from 'react'
import { Box } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'

interface FileDropZoneProps {
    isDragging: boolean
    dropHandlers: {
        onDragEnter: (e: DragEvent<HTMLElement>) => void
        onDragOver: (e: DragEvent<HTMLElement>) => void
        onDragLeave: (e: DragEvent<HTMLElement>) => void
        onDrop: (e: DragEvent<HTMLElement>) => void
    }
    inputRef: RefObject<HTMLInputElement>
    handleInputChange: (e: ChangeEvent<HTMLInputElement>) => void
    accept?: string
    multiple?: boolean
    inputName: string
    className?: string
    openPicker?: () => void
    hasError?: boolean
    sx?: SxProps<Theme>
    children: ReactNode
}

export function FileDropZone({
    isDragging,
    dropHandlers,
    inputRef,
    handleInputChange,
    accept,
    multiple,
    inputName,
    className,
    openPicker,
    hasError,
    sx,
    children,
}: FileDropZoneProps) {
    const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
        if (!openPicker) return

        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            openPicker()
        }
    }

    return (
        <Box
            component="section"
            className={['fakeInputField', className].filter(Boolean).join(' ')}
            {...dropHandlers}
            onClick={openPicker}
            onKeyDown={handleKeyDown}
            role={openPicker ? 'button' : undefined}
            tabIndex={openPicker ? 0 : undefined}
            sx={[
                {
                    position: 'relative',
                    ...(isDragging && {
                        border: '2px dashed',
                        borderColor: 'primary.main',
                    }),
                    ...(hasError &&
                        !isDragging && {
                            borderColor: '#dc3545',
                        }),
                    backgroundColor: isDragging ? 'action.hover' : 'inherit',
                    minHeight: 60,
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    cursor: openPicker ? 'pointer' : 'default',
                    '&:focus-visible': {
                        outline: (theme) =>
                            `3px solid ${theme.palette.primary.main}`,
                        outlineOffset: '2px',
                    },
                    ...(hasError && {
                        '&:focus': {
                            borderColor: '#dc3545',
                            boxShadow: '0 0 0 0.2rem rgba(220, 53, 69, 0.25)',
                        },
                    }),
                },
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}
        >
            <input
                name={inputName}
                accept={accept}
                onChange={handleInputChange}
                type="file"
                multiple={multiple}
                ref={inputRef}
                style={{ display: 'none' }}
            />
            {children}
        </Box>
    )
}
